import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { ButtonAttributes } from '@celebration/design-system/dist/lib/button-group/props.types';
import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { ZodFormattedError } from 'zod';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { useTranslation } from 'react-i18next';
import FullPageFlow from '../../../../common/components/full-page-flow';
import BranchFormInfos from './components/branch-form-infos/branch-form-infos';
import IBranchFormData from '../../../../common/interfaces/IBranchFormData';
import BranchFormSteps from './enums/steps.enum';
import BranchFormService from './services/branch-form.service';
import IBranchFormDataErrors, {
    IContactErrors,
} from '../../interfaces/IBranchFormDataErrors';
import IContact, {
    ICarriersContact,
} from '../../../../common/interfaces/IContacts';
import IBranchFormFullPageFlowProps from './interfaces/IBranchFormFullPageFlowProps';
import IHandleFormChangeProps, {
    EventType,
} from './interfaces/IHandleFormChangeProps';
import BranchFormRegions from './components/branch-form-regions/index';
import { IRegionLocations } from '../../../../common/interfaces/IRegionLocations';
import Overview from '../../../../common/components/branches/overview';
import CarriersService from '../../../carriers/services/carriers.service';
import { ILocation } from '../../../../common/interfaces/ILocation';
import BranchService from '../../../../common/services/branch/branch.service';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import { ICarrierData } from '../../interfaces/ICarrierData';
import { DesserializerDecimal } from '../../../../utils/formatterDecimal';
import Auth from '../../../../app/contexts/auth';

function BranchFormFullPageFlow({
    carrier,
    isBranchFormOpened,
    setIsBranchFormOpened,
    branchId,
    setEditBranchId,
    fetchBranchesData,
}: Readonly<IBranchFormFullPageFlowProps>) {
    const { setToast } = useToast();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>();
    const generalInfoValidatedOnce = useRef(false);

    const [initialStateCarrier, setInitialStateCarrier] = useState<
        ICarrierData | undefined
    >();
    const [currentBranchData, setCurrentBranchData] = useState<
        IBranchFormData | undefined
    >();

    const emptyErrors = useMemo(() => {
        return {} as ZodFormattedError<IContactErrors>;
    }, []);
    const [errors, setErrors] = useState(emptyErrors);

    const [isExitModalOpen, setIsExitModalOpen] = useState(false);
    const [isRegionModifiedOpenModal, setIsRegionModifiedOpenModal] =
        useState(false);

    const [
        isShowingBranchFormConfirmationModal,
        setIsShowingBranchFormConfirmationModal,
    ] = useState(false);

    const { user } = Auth.useAuth();

    const emptyBranchData = useMemo(() => {
        return {
            additionalContacts: [] as IContact[],
            mainContact: {} as IContact,
            carriersContact:
                initialStateCarrier?.carrierContact ?? ({} as ICarriersContact),
            paymentTerm: DesserializerDecimal(initialStateCarrier?.paymentTerm),
        } as IBranchFormData;
    }, [initialStateCarrier]);

    const [activeStep, setActiveStep] = useState<BranchFormSteps>(
        BranchFormSteps.info,
    );
    const [branchData, setBranchData] =
        useState<IBranchFormData>(emptyBranchData);
    const regionData = useRef<IRegionLocations[]>([]);

    window.onbeforeunload = (e: any) => {
        if (isBranchFormOpened) {
            e.preventDefault();
            return e;
        }
    };

    const validateGeneralInfo = useCallback(
        (branchData: IBranchFormData) => {
            const gereneralInfoResult = BranchFormService.validateGeneralInfo(
                branchData,
                user?.type !== 'ambev' ||
                    mainContactPartiallyFilled(branchData.mainContact),
            );

            if (!gereneralInfoResult || !gereneralInfoResult.success) {
                setErrors(
                    gereneralInfoResult.errors as ZodFormattedError<IBranchFormDataErrors>,
                );
                return false;
            }

            return true;
        },
        [user?.type],
    );

    const mainContactPartiallyFilled = (mainContact: IContact): boolean =>
        !!mainContact.email ||
        !!mainContact.phone ||
        !!mainContact.responsible ||
        !!mainContact.occupation;

    const revalidateGeneralInfo = useCallback(
        (branchData: IBranchFormData) => {
            if (generalInfoValidatedOnce.current) {
                if (!validateGeneralInfo(branchData)) return;

                setErrors(emptyErrors);
            }
        },
        [validateGeneralInfo, emptyErrors],
    );

    const validateSystemCodes = useCallback(
        async (branchData: IBranchFormData) => {
            const systemCodesResult =
                await BranchFormService.validateSystemCodes(branchData);

            if (!systemCodesResult || !systemCodesResult.success) {
                setErrors(
                    systemCodesResult.errors as ZodFormattedError<IBranchFormDataErrors>,
                );
                return false;
            }

            return true;
        },
        [],
    );

    const validateInfoStep = useCallback(
        async (branchData: IBranchFormData) => {
            generalInfoValidatedOnce.current = true;

            if (!validateGeneralInfo(branchData)) {
                return false;
            }

            if (!(await validateSystemCodes(branchData))) {
                return false;
            }

            setErrors(emptyErrors);

            return true;
        },
        [emptyErrors, validateGeneralInfo, validateSystemCodes],
    );

    const fetchBranchData = useCallback(
        async (carrierId: string, branchId: string) => {
            try {
                const branch = await BranchService.getBranch(
                    carrierId,
                    branchId,
                    t,
                );
                branch.carriersContact = branchData.carriersContact;

                await validateInfoStep(branch);

                setCurrentBranchData(branch);
                setBranchData(branch);
            } catch (error: any) {
                setToast('negative', t('Erro'), (error as Error).message);
            } finally {
                setIsLoading(false);
            }
        },
        [branchData.carriersContact, validateInfoStep, setToast, t],
    );

    const fetchCarriersInfo = useCallback(
        async (carrierId: string) => {
            const data = await BranchFormService.getCarriersInfo(carrierId);

            const updatedBranchData = branchData;
            updatedBranchData.paymentTerm = data.paymentTerm;
            updatedBranchData.carriersContact = data.carrierContact;

            setBranchData(updatedBranchData);
            setInitialStateCarrier(data);
        },
        [branchData],
    );

    const handleFormChange = useCallback(
        (handleFormChangeProps: IHandleFormChangeProps) => {
            let updatedBranchData = { ...branchData };

            if (!handleFormChangeProps.eventType) {
                updatedBranchData = {
                    ...branchData,
                    [handleFormChangeProps.property]:
                        handleFormChangeProps.newValue,
                };
            }
            switch (handleFormChangeProps.eventType) {
                case EventType.CarriersContactChange:
                    updatedBranchData.carriersContact = {
                        ...branchData.carriersContact,
                        [handleFormChangeProps.property]:
                            handleFormChangeProps.newValue,
                    };
                    break;
                case EventType.MainContactChange:
                    updatedBranchData.mainContact = {
                        ...branchData.mainContact,
                        [handleFormChangeProps.property]:
                            handleFormChangeProps.newValue,
                    };
                    break;
                case EventType.AdditionalContactChange:
                    if (handleFormChangeProps.index === undefined) break;

                    updatedBranchData.additionalContacts[
                        handleFormChangeProps.index
                    ] = {
                        ...branchData.additionalContacts[
                            handleFormChangeProps.index
                        ],
                        [handleFormChangeProps.property]:
                            handleFormChangeProps.newValue,
                    };
                    break;
            }
            revalidateGeneralInfo(updatedBranchData);
            setBranchData(updatedBranchData);
        },
        [branchData, revalidateGeneralInfo],
    );

    const addNewAdditionalContact = useCallback(() => {
        setBranchData({
            ...branchData,
            additionalContacts: [
                ...branchData.additionalContacts,
                {} as IContact,
            ],
        });
    }, [branchData]);

    const fullPageFlowTitle = useMemo(() => {
        const step_title = branchId
            ? t('FilialEditarCnpj')
            : t('FilialNovoCnpj');

        switch (activeStep) {
            case BranchFormSteps.info:
                return `${step_title} - ${t('FilialEtapa1')}`;
            case BranchFormSteps.regions:
                return `${step_title} - ${t('FilialEtapa2')}`;
            case BranchFormSteps.review:
                return `${step_title} - ${t('FilialEtapa3')}`;
        }
    }, [activeStep, branchId, t]);

    const closeBranchFormHandler = useCallback(() => {
        setIsExitModalOpen(true);
    }, []);

    const closeExitModalHandler = useCallback(() => {
        setIsExitModalOpen(false);
    }, []);

    const closeModalHandler = useCallback(() => {
        regionData.current = [] as IRegionLocations[];
        setIsExitModalOpen(false);
        setIsBranchFormOpened(false);
        setActiveStep(BranchFormSteps.info);
        setBranchData(emptyBranchData);
        setEditBranchId(undefined);
        setCurrentBranchData(undefined);
        setErrors(emptyErrors);
        generalInfoValidatedOnce.current = false;
    }, [emptyErrors, emptyBranchData, setIsBranchFormOpened, setEditBranchId]);

    const showBranchFormConfirmationModal = useCallback(() => {
        setIsShowingBranchFormConfirmationModal(true);
    }, []);

    const closeBranchFormConfirmationModal = useCallback(() => {
        setIsShowingBranchFormConfirmationModal(false);
    }, []);

    const closeBackConfirmationModal = useCallback(() => {
        setIsRegionModifiedOpenModal(false);
    }, []);

    const goToPreviousStep = useCallback(() => {
        setActiveStep(activeStep - 1);
    }, [activeStep]);

    const goToRegionsStep = useCallback(async () => {
        setIsLoading(true);
        const validInfo = await validateInfoStep(branchData);

        setIsLoading(false);

        if (validInfo) {
            setActiveStep(BranchFormSteps.regions);
        }
    }, [branchData, validateInfoStep]);

    const goToReviewStep = useCallback(() => {
        const regionsStepIsValid = true;
        if (regionsStepIsValid) {
            setActiveStep(BranchFormSteps.review);
        }
    }, []);

    const goToNextStep = useCallback(() => {
        activeStep === BranchFormSteps.info
            ? goToRegionsStep()
            : goToReviewStep();
    }, [activeStep, goToRegionsStep, goToReviewStep]);

    const goToInfoStep = useCallback(() => {
        regionData.current = [] as IRegionLocations[];
        setIsRegionModifiedOpenModal(false);
        goToPreviousStep();
    }, [goToPreviousStep]);

    const anyLocationChecked = () => {
        return regionData.current.some(region =>
            region.locations.some(location => location.isChecked),
        );
    };

    const backToInfoStep = useCallback(() => {
        if (
            activeStep === BranchFormSteps.regions &&
            regionData.current.length > 0 &&
            anyLocationChecked()
        ) {
            setIsRegionModifiedOpenModal(true);
        } else {
            goToPreviousStep();
        }
    }, [activeStep, setIsRegionModifiedOpenModal, goToPreviousStep]);

    const getErrorMessage = useCallback(
        (e: any) => {
            const action = branchId
                ? t('EdicaoMinusculo')
                : t('AdicaoMinusculo');

            let message = `${t('FilialErroDurante')} ${action} ${t('FilialDaFilial')}`;

            if (e instanceof Error) {
                message = e.message;
            }
            return message;
        },
        [branchId, t],
    );

    const getLocationsIds = useCallback(() => {
        const locationsIds: string[] = [];

        regionData.current.forEach((currentElement: IRegionLocations) => {
            currentElement.locations.forEach((location: ILocation) => {
                if (location.isChecked) {
                    locationsIds.push(location.id);
                }
            });
        });

        return locationsIds;
    }, []);

    const getCheckedRegions = (regionData: IRegionLocations[]) => {
        const filteredRegions = regionData.filter((current: IRegionLocations) =>
            current.locations.find((location: ILocation) => location.isChecked),
        );
        return filteredRegions.map(currentRegion => {
            return {
                name: currentRegion.name,
                abbreviation: currentRegion.abbreviation,
                id: currentRegion.id,
                locations: currentRegion.locations.filter(f => f.isChecked),
                isChecked: currentRegion.isChecked,
            } as IRegionLocations;
        });
    };

    const submitBranchData = useCallback(async () => {
        setIsShowingBranchFormConfirmationModal(false);
        setIsLoading(true);

        let toastError = true;

        try {
            if (
                branchData.carriersContact !==
                initialStateCarrier?.carrierContact
            ) {
                await CarriersService.updateCarrierContact(
                    carrier.carrierId,
                    branchData.carriersContact,
                );
            }

            const responseBranchId = await BranchFormService.saveCarrierBranch(
                carrier.carrierId,
                branchData,
                branchId,
            );

            toastError = false;

            await BranchFormService.updateBranchLocations(
                carrier.carrierId,
                responseBranchId,
                getLocationsIds(),
            );

            const action = branchId
                ? t('FilialAtualizada')
                : t('FilialCadastrada');

            setToast(
                'positive',
                t('Sucesso'),
                `${action} com  ${t('ComSucesso')}`,
            );
        } catch (e: any) {
            if (toastError) {
                setToast('negative', t('Erro'), getErrorMessage(e));
            } else {
                setToast('warning', t('Atencao'), getErrorMessage(e));
            }
        } finally {
            if (
                branchData.carriersContact !==
                initialStateCarrier?.carrierContact
            ) {
                setInitialStateCarrier(undefined);
            }

            fetchBranchesData();
            closeModalHandler();
            setIsLoading(false);
        }
    }, [
        branchId,
        carrier.carrierId,
        initialStateCarrier,
        closeModalHandler,
        branchData,
        getLocationsIds,
        getErrorMessage,
        fetchBranchesData,
        setToast,
        t,
    ]);

    const headerFullPageFlowButtonList: ButtonAttributes[] = useMemo(() => {
        const action = branchId
            ? t('FilialEdicaoFrase')
            : t('FilialFraseCadastroDeUma');

        const headerButtons: ButtonAttributes[] = [];

        if (activeStep === BranchFormSteps.info) {
            headerButtons.push({
                id: 'close-button',
                label: t('Fechar'),
                disabled: false,
                loading: false,
                title: `${t('Fechar')} ${action} ${t('FilialMinusculo')}`,
                ariaLabel: `${t('Fechar')} ${action} ${t('FilialMinusculo')}`,
                dataTestid: 'close-button',
                callback: closeBranchFormHandler,
            });
        }

        if (activeStep === BranchFormSteps.regions) {
            headerButtons.push({
                id: 'back-button',
                label: t('Voltar'),
                disabled: false,
                loading: false,
                title: t('VoltarCadastroAnterior'),
                ariaLabel: t('VoltarCadastroAnterior'),
                dataTestid: 'back-button',
                callback: backToInfoStep,
            });
        }

        if (activeStep === BranchFormSteps.review) {
            headerButtons.push({
                id: 'back-button',
                label: t('Voltar'),
                disabled: false,
                loading: false,
                title: t('VoltarCadastroAnterior'),
                ariaLabel: t('VoltarCadastroAnterior'),
                dataTestid: 'back-button',
                callback: goToPreviousStep,
            });
        }

        if (
            activeStep === BranchFormSteps.info ||
            activeStep === BranchFormSteps.regions
        ) {
            headerButtons.push({
                id: 'next-button',
                label: t('Avançar'),
                disabled: false,
                loading: false,
                title: t('AvancarCadastro'),
                ariaLabel: t('AvancarCadastro'),
                dataTestid: 'next-button',
                callback: goToNextStep,
            });
        }

        if (activeStep === BranchFormSteps.review) {
            headerButtons.push({
                id: 'finish-button',
                label: t('Finalizar'),
                disabled: false,
                loading: false,
                title: `Finalizar ${action} filial`,
                ariaLabel: `Finalizar ${action} filial`,
                dataTestid: 'finish-button',
                callback: showBranchFormConfirmationModal,
            });
        }

        return headerButtons;
    }, [
        activeStep,
        closeBranchFormHandler,
        goToPreviousStep,
        goToNextStep,
        backToInfoStep,
        showBranchFormConfirmationModal,
        branchId,
        t,
    ]);

    const renderExitConfirmationModal = () => {
        const action = branchId ? 'Edição' : 'Adição';

        if (isExitModalOpen) {
            return (
                <ClbModal
                    title={`${t('Cancelar')} ${t(action)} ${t('de')} CNPJ?`}
                    open={isExitModalOpen}
                    onClose={() => closeExitModalHandler()}
                    description={t('FilialSairEdicao')}
                    type="danger"
                    itemsFooter={[
                        {
                            key: 'exit-button',
                            id: 'exit-button',
                            label: t('Sair'),
                            disabled: false,
                            loading: false,
                            title: t('FilialSairCadastro'),
                            'data-testid': 'modal-exit-button',
                            onClick: _ => closeModalHandler(),
                            role: 'button',
                            size: 'md',
                            styleType: 'danger',
                            icon: 'cancel',
                            iconPosition: 'left',
                        },
                        {
                            key: 'back-button',
                            id: 'back-button',
                            label: t('Voltar'),
                            disabled: false,
                            loading: false,
                            title: t('FilialVoltarCadastro'),
                            'data-testid': 'modal-back-button',
                            onClick: _ => setIsExitModalOpen(false),
                            role: 'button',
                            size: 'md',
                            styleType: 'secondary',
                            icon: 'blocked',
                            iconPosition: 'left',
                        },
                    ]}
                />
            );
        }
    };

    const renderBackConfirmationModal = useCallback(() => {
        if (isRegionModifiedOpenModal) {
            return (
                <ClbModal
                    title={t('FilialCancelarEdicao')}
                    open
                    onClose={() => closeBackConfirmationModal()}
                    description={t('FilialCancelarEdicaoFrase')}
                    type="danger"
                    itemsFooter={[
                        {
                            key: 'modified-regions-modal-modal-exit-button',
                            id: 'modified-regions-modal-modal-exit-button',
                            label: t('FilialInterromper'),
                            disabled: false,
                            loading: false,
                            title: t('FilialVoltarDados'),
                            'data-testid':
                                'modified-regions-modal-modal-exit-button',
                            onClick: () => goToInfoStep(),
                            role: 'button',
                            size: 'md',
                            styleType: 'danger',
                            icon: 'cancel',
                            iconPosition: 'left',
                        },
                        {
                            key: 'modified-regions-modal-cancel-button',
                            id: 'modified-regions-modal-cancel-button',
                            label: t('Cancelar'),
                            disabled: false,
                            loading: false,
                            title: t('FilialCancelarAcao'),
                            'data-testid':
                                'modified-regions-modal-cancel-button',
                            onClick: () => closeBackConfirmationModal(),
                            role: 'button',
                            size: 'md',
                            styleType: 'secondary',
                            icon: 'blocked',
                            iconPosition: 'left',
                        },
                    ]}
                />
            );
        }
    }, [
        isRegionModifiedOpenModal,
        closeBackConfirmationModal,
        goToInfoStep,
        t,
    ]);

    const renderBranchFormConfirmationModal = () => {
        const action = branchId ? t('AtualizandoMinusculo') : t('Criacao');

        const modalContent = branchId
            ? t('FilialAtualizando')
            : t('filialGerando');

        if (isShowingBranchFormConfirmationModal) {
            return (
                <ClbModal
                    title={`Confirmar ${action} de filial?`}
                    open
                    onClose={() => closeBranchFormConfirmationModal()}
                    description={`${t('FilialAoFinalizar')} ${modalContent} ${t('TransportadoraMinusculo')}
                            ${carrier.carrierName}. ${t('DesejaContinuar')}`}
                    itemsFooter={[
                        {
                            key: 'new-branch-confirmation-model-yes-button',
                            id: 'new-branch-confirmation-model-yes-button',
                            label: t('Sim'),
                            disabled: false,
                            loading: false,
                            title: `${t('Confirmar')} ${action}`,
                            'data-testid':
                                'new-branch-confirmation-modal-yes-button',
                            onClick: async () => await submitBranchData(),
                            role: 'button',
                            size: 'md',
                        },
                        {
                            key: 'new-branch-confirmation-model-no-button',
                            id: 'new-branch-confirmation-model-no-button',
                            label: t('Não'),
                            disabled: false,
                            loading: false,
                            title: `${t('RetornarA')} ${action} ${t('FilialDeFilial')}`,
                            'data-testid':
                                'new-branch-confirmation-modal-no-button',
                            onClick: () => closeBranchFormConfirmationModal(),
                            role: 'button',
                            size: 'md',
                            styleType: 'secondary',
                            icon: 'blocked',
                            iconPosition: 'left',
                        },
                    ]}
                />
            );
        }
    };

    useEffect(() => {
        if (initialStateCarrier === undefined && carrier.carrierId) {
            fetchCarriersInfo(carrier.carrierId);
        }
    }, [carrier.carrierId, initialStateCarrier, fetchCarriersInfo]);

    useEffect(() => {
        if (currentBranchData === undefined && branchId) {
            setIsLoading(true);
            fetchBranchData(carrier.carrierId, branchId);
        }
    }, [carrier.carrierId, branchId, fetchBranchData, currentBranchData]);

    return (
        <>
            <FullPageFlow
                title={fullPageFlowTitle}
                type="progress"
                opened={isBranchFormOpened}
                onClbClose={closeBranchFormHandler}
                steps={2}
                buttonsList={headerFullPageFlowButtonList}
                activeStep={activeStep}
                data-testid="full-page-flow"
            >
                {isLoading && <ClbLoading />}

                {isBranchFormOpened &&
                    activeStep === BranchFormSteps.info &&
                    !isLoading && (
                        <BranchFormInfos
                            handleFormChange={handleFormChange}
                            branchFormData={branchData}
                            initialStateCarrier={
                                initialStateCarrier ?? ({} as ICarrierData)
                            }
                            addNewAdditionalContact={addNewAdditionalContact}
                            inputErrors={errors}
                        />
                    )}

                {isBranchFormOpened &&
                    activeStep === BranchFormSteps.regions &&
                    !isLoading && (
                        <BranchFormRegions
                            branchFormData={branchData}
                            regionData={regionData}
                            carrierId={carrier.carrierId}
                            carrierContactData={
                                initialStateCarrier?.carrierContact
                            }
                        />
                    )}

                {isBranchFormOpened &&
                    activeStep === BranchFormSteps.review &&
                    !isLoading && (
                        <Overview
                            branchData={branchData}
                            regionData={getCheckedRegions(regionData.current)}
                            carrierContactData={
                                initialStateCarrier?.carrierContact
                            }
                        />
                    )}
            </FullPageFlow>

            {renderExitConfirmationModal()}
            {renderBackConfirmationModal()}
            {renderBranchFormConfirmationModal()}
        </>
    );
}

export default BranchFormFullPageFlow;
